import { useEffect, useRef, useState } from 'react';
import { useHover, useOnClickOutside } from 'usehooks-ts';

const useExploreMoreHook = ({ streamData }: any) => {
  const dropdownRef: any = useRef(null);
  const dropdownRef2: any = useRef(null);
  const isHovering = useHover(dropdownRef ?? dropdownRef2);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStreamIndex, setSelectedStreamIndex] = useState(streamData?.[0]?.id);
  useOnClickOutside(dropdownRef, () => {
    setShowDropdown(false);
    setSelectedStreamIndex(streamData?.[0]?.id);
  });

  // Handle opening/closing of the dropdown
  const handleCloseDropdown = () => {
    setShowDropdown(false);
    if (isHovering) {
      setTimeout(() => {
        dropdownRef.current.dispatchEvent(new MouseEvent('mouseleave'));
      }, 0);
    }
  };

  const handleStreamSelect = (index: number) => {
    setSelectedStreamIndex(index);
  };

  useEffect(() => {
    if (!isHovering) {
      setSelectedStreamIndex(streamData?.[0]?.id);
    }
  }, [isHovering]);
  // Prevent background scroll when dropdown is open
  useEffect(() => {
    if (showDropdown) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [showDropdown]);

  useEffect(() => {
    if (streamData && streamData.length > 0) {
      setSelectedStreamIndex(streamData[0]?.id);
    }
  }, [streamData]);

  const streams = streamData ?? [];
  const selectedStream = streams.find((stream: any) => stream.id === selectedStreamIndex);
  const colleges = selectedStream?.college_names ?? [];
  const courses = selectedStream?.courses ?? [];
  const states = selectedStream?.geo_states ?? [];
  const cities = selectedStream?.geo_cities ?? [];
  const countries =
    selectedStream?.geo_countries?.filter((country: any) => !country?.country_name?.toLowerCase().includes('india')) ??
    [];
  const exams = selectedStream?.exams ?? [];
  const stream_url = selectedStream?.stream_name?.replace(/\s+/g, '-')?.toLowerCase();

  const isManagementSelected = selectedStream?.stream_name?.toLowerCase() === "management";

  const pgdmCities = isManagementSelected
    ? (selectedStream.pgdm_city || [])
      .sort((a: any, b: any) => Number(a.display_sequence) - Number(b.display_sequence))
      .map((city: any) => ({
        ...city,
        name: city.name || `PGDM Colleges in ${city.city_name}`,
      }))
    : [];

  const pgdmStates = isManagementSelected
    ? (selectedStream.pgdm_states || [])
      .sort((a: any, b: any) => Number(a.display_sequence) - Number(b.display_sequence))
      .map((state: any) => ({
        ...state,
        name: state.name || `PGDM Colleges in ${state.state_name}`,
      }))
    : [];

  let sections;

  if (isManagementSelected) {
    sections = [
      {
        id: 1,
        title: `Top Colleges in ${selectedStream?.stream_name}`,
        items: colleges,
        key: 'college_name',
        getUrl: (item: any) =>
          item?.is_online_college
            ? `/colleges/${item?.college_url}`
            : `/colleges/${item?.college_url}/${item?.college_navbars[0]?.title?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/${stream_url}-colleges-in-india`,
      },

      {
        id: 3,
        title: 'PGDM Colleges (Cities)',
        items: pgdmCities,
        key: 'name',
        getUrl: (item: any) => `/${item.city_url}`,
        viewAllUrl: `/pgdm-colleges-in-india`,
      },
      {
        id: 4,
        title: 'PGDM Colleges (States)',
        items: pgdmStates,
        key: 'name',
        getUrl: (item: any) => `/${item.state_url}`,
        viewAllUrl: `/pgdm-colleges-in-india`,
      },
      {
        id: 5,
        title: 'Courses',
        items: courses,
        key: 'course_name',
        getUrl: (item: any) => `/${item?.course_url?.replace(/\s+/g, '-')?.toLowerCase()}-colleges-in-india`,
        viewAllUrl: `/courses/${stream_url}`,
      },
      {
        id: 6,
        title: 'Exams',
        items: exams,
        key: 'abbreviation',
        getUrl: (item: any) =>
          `/exams/${item.exam_url}/${item?.exam_navbars[0]?.title?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/exams/${stream_url}`,
      },
    ];
  } else {
    sections = [
      {
        id: 1,
        title: `Top Colleges in ${selectedStream?.stream_name}`,
        items: colleges,
        key: 'college_name',
        getUrl: (item: any) =>
          item?.is_online_college
            ? `/colleges/${item?.college_url}`
            : `/colleges/${item?.college_url}/${item?.college_navbars[0]?.title?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/${stream_url}-colleges-in-india`,
      },
      // {
      //   id: 2,
      //   title: "Specializations",
      //   items: specializations,
      //   key: "abbreviation",
      //   getUrl: (item: any) =>
      //     `/${item?.abbreviation
      //       ?.replace(/\s+/g, "-")
      //       ?.toLowerCase()}-colleges-in-india`,
      //   viewAllUrl: `/${stream_url}-colleges-in-india`,
      // },
      {
        id: 3,
        title: 'Cities',
        items: cities,
        key: 'city_name',
        getUrl: (item: any) => `/${stream_url}-colleges-in-${item?.city_url?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/${stream_url}-colleges-in-india`,
      },
      {
        id: 4,
        title: 'Study Abroad',
        items: countries,
        key: 'country_name',
        getUrl: (item: any) =>
          item?.country_url
            ? `/${stream_url}-${item.country_url?.replace('study-in-', 'colleges-in-')?.toLowerCase()}`
            : '#',
        viewAllUrl: `/${stream_url}-colleges-in-abroad`,
      },
      {
        id: 5,
        title: 'Courses',
        items: courses,
        key: 'course_name',
        getUrl: (item: any) => `/${item?.course_url?.replace(/\s+/g, '-')?.toLowerCase()}-colleges-in-india`,
        viewAllUrl: `/courses/${stream_url}`,
      },
      {
        id: 6,
        title: 'States',
        items: states,
        key: 'state_name',
        getUrl: (item: any) => `/${stream_url}-colleges-in-${item?.state_url?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/${stream_url}-colleges-in-india`,
      },
      {
        id: 7,
        title: 'Exams',
        items: exams,
        key: 'abbreviation',
        getUrl: (item: any) =>
          `/exams/${item.exam_url}/${item?.exam_navbars[0]?.title?.replace(/\s+/g, '-')?.toLowerCase()}`,
        viewAllUrl: `/exams/${stream_url}`,
      },
    ];
  }


  return {
    dropdownRef,
    setShowDropdown,
    showDropdown,
    dropdownRef2,
    isHovering,
    handleCloseDropdown,
    sections,
    streams,
    handleStreamSelect,
    setSelectedStreamIndex,
    selectedStreamIndex,
  };
};

export default useExploreMoreHook;
